import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { gql } from '@apollo/client';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';

import {
  Box,
  Button,
  Grid,
  GridItem,
  Heading,
  VStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Checkbox,
} from '@chakra-ui/react';
import { Provider } from '../../../Chakra';
import Loader from '../../../Components/Loader';
import { EVENT_TITLE } from '../../../Constants/AppConstants';
import useHeaderComponentsMutation from '../../../Hooks/useHeaderComponentsMutation';

import { AnnouncementCard } from '../EventAnnouncements/AnnouncementCard';
import { PublicScheduleList } from './PublicScheduleList';
import { SpiritPointsLeaderboard } from './SpiritPointsLeaderboard';
import GEAR_ICON from '../../../Images/icons-svg/gear-black.svg';

const PUBLIC_EVENT_QUERY = gql`
query fetchEventDetail($id: Int!) {
  fetchEventDetail(id: $id) {
    id
    title
    showSpiritPointLeaderboard
    spiritPointRankings(limit: 5) {
      teams {
        id
        code
        totalSpiritPoints
        school {
          id
          name
        }
      }
    }
    announcements {
      id
      title
      body
      link
      targetType
      targetId
      isImportant
      createdAt
      fetchSchools {
        id
        name
      }
      fetchCompetitions {
        id
        title
      }
      fetchRoles {
        id
        title
      }
    }
    schedule {
      scheduleEvents {
        id
        title
        description
        type
        startTime
        endTime
        room
        fetchAssociatedEventCompetition {
          id
          title
        }
        fetchLocation {
          id
          name
        }
      }
    }
    fetchEventCompetitions {
      id
      title
    }
    locations {
      id
      name
    }
  }
}
`;

const LiveEventPage = () => {
  const [showLeaderboard, setShowLeaderboard] = useState(true);
  const handle = useFullScreenHandle();
  const { id } = useParams();
  const eventId = parseInt(id, 10);

  const { data, loading } = useQuery(PUBLIC_EVENT_QUERY, {
    variables: { id: eventId },
    fetchPolicy: 'network-only',
    pollInterval: 15000,
  });
  const event = data?.fetchEventDetail;
  const isLeaderboardVisible = event?.showSpiritPointLeaderboard && showLeaderboard;

  useHeaderComponentsMutation({
    title: 'Live Dashboard',
    components: [{ key: EVENT_TITLE, value: event?.title }],
    backLink: `/event-manage/${eventId}`,
  });

  if (loading) {
    return <Loader />;
  }

  const announcements = [...(event?.announcements ?? [])]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .filter((a) => a.targetType === 'all' || a.targetType === 'competition')
    ?? [];

  return (
    <>
      <Provider>
        <Box margin={2}>
          <Button onClick={handle.enter} size="lg">
            {/* // eslint-disable-next-line jsx-a11y/accessible-emoji */}
            Click here to fullscreen me 🎉
          </Button>
          {event?.showSpiritPointLeaderboard && (
            <Popover placement="bottom-start">
              <PopoverTrigger>
                <Button size="lg" variant="outline" padding={2}>
                  <img src={GEAR_ICON} alt="Settings" style={{ width: '20px', height: '20px' }} />
                </Button>
              </PopoverTrigger>
              <PopoverContent width="250px">
                <PopoverArrow />
                <PopoverBody>
                  <Checkbox
                    isChecked={showLeaderboard}
                    onChange={(e) => setShowLeaderboard(e.target.checked)}
                  >
                    Show Spirit Points Leaderboard
                  </Checkbox>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </Box>
        <FullScreen handle={handle}>
          <Grid
            templateColumns="3fr 2fr"
            templateRows={isLeaderboardVisible ? 'auto auto auto 1fr' : 'auto 1fr'}
            width="100%"
            height="100%"
            background="white"
            padding={handle.active ? 16 : 4}
            paddingBottom={4}
            columnGap={16}
            rowGap={4}
          >
            {isLeaderboardVisible && (
              <SpiritPointsLeaderboard teams={event?.spiritPointRankings?.teams} />
            )}

            <GridItem gridArea={isLeaderboardVisible ? '3 / 1 / 4 / 2' : '1 / 1 / 2 / 2'}>
              <Heading size="md">Latest Announcements</Heading>
            </GridItem>

            <GridItem gridArea={isLeaderboardVisible ? '4 / 1 / 5 / 2' : '2 / 1 / 3 / 2'} overflow="scroll">
              <VStack spacing={4} paddingBottom={4}>
                {announcements.map((announcement, idx) => (
                  <AnnouncementCard
                    announcement={announcement}
                    key={announcement.id}
                    highlight={idx === 0}
                  />
                ))}
              </VStack>
            </GridItem>

            <GridItem gridArea="1 / 2 / 2 / 3">
              <Heading size="md">Upcoming Schedule</Heading>
            </GridItem>
            <GridItem gridArea={isLeaderboardVisible ? '2 / 2 / 5 / 3' : '2 / 2 / 3 / 3'} overflow="scroll">
              <PublicScheduleList
                scheduleEvents={event?.schedule?.scheduleEvents ?? []}
                shouldScroll={handle.active}
              />
            </GridItem>
          </Grid>
        </FullScreen>
      </Provider>
    </>
  );
};

export default LiveEventPage;
